// Nairobi, "Giraffes under the city lights": the blue hour after the short rains, on the central plains of Nairobi
// National Park at a waterhole (about 1.380 S, 36.870 E, 1,620 m), looking north-west (about 328°) at the city
// 10.6 to 11.4 km away. Seen from there, left to right (bearings from the viewpoint): Wilson Airport's beacon low on
// the left (316°); Upper Hill: Britam Tower, the tallest, its prism-shaped slab and 60 m lattice mast (324.5°,
// 10.9 km), an office slab in front of its foot, UAP Old Mutual Tower with its hipped crown and spire (327.8°);
// then the CBD: Times Tower (332.6°) and, 0.3° to its right and a little further, the KICC's terracotta cylinder and
// saucer (332.9°, 11.4 km), its upper half showing over the Industrial Area's low roofs (its cone-roofed amphitheatre
// is too low to show from the park). The SGR viaduct to Naivasha crosses the whole view 1.6 km out, its deck just
// under the horizon; a billboard stands on its pole by the Southern Bypass at the park's edge. The bearings are
// compressed into the frame without changing the order (telephoto, 5 px per milliradian; the gap from Times Tower to
// the KICC is kept true), and the heights are the real angles: Britam's tip 26.9 mrad over the eye, UAP's 22.2,
// Times Tower's 17.6, the KICC's 13.2.
// Back to front: deep blue sky with the afterglow fading in the west (left), stars, a young crescent moon lying on
// its back -> the city's glow, the far towers, Upper Hill and the CBD, the city's lit edge -> the viaduct -> the far
// plain -> the waterhole mirroring the city -> Maasai giraffes, one browsing a flat-topped acacia, two at the water
// -> the near grass, where a night game drive has stopped for chai: a Land Cruiser with its roof up, a camp table
// with a hurricane lantern, the guests and a ranger.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, ditherFill, makeHalo, drawHalo, glyph, FONT3, reflect, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };

/* ================= Layout ================= */
// A low camera (1.25 m, someone sitting on the car's step): the stop's people, 21 px a metre, stand with their heads
// over the horizon, and the giraffes' necks and the acacia rise against the city's glow and the afterglow.
const EYE = 180;                    // the eye line (the far plain rises a little to the park's edge)
const VIA = 178;                    // top of the SGR viaduct's deck, just over the eye line
const PLAIN = 182;                  // the far plain shows under the deck, between the piers
const POOL = { x0: 92, x1: 300, top: 186, bot: 197 };    // the waterhole (its banks are drawn over the rectangle)
const FEET = 206;                   // the stop's people stand on this row (above the bottom 63 rows)
const MOON = { x: 72, y: 40, r: 7 };
// Heights are the real angles over the eye line at 5.5 px/mrad. Britam Tower (2018): 200.1 m with its 60 m mast
// (0.30), the slab about 34 m wide (5.9:1); at 10.9 km its tip is 148 px over the eye line, the roof 118, its foot
// (Upper Hill, 93 m over the viewpoint) 47; 18 px wide low down, 16 under the roof.
const BRIT = { cx: 150, tip: 32, roof: 62, base: 133, hw: 9 };
// UAP Old Mutual Tower (2016): 163 m; the spire 0.13, the hipped crown 0.09, the body 0.78; about 40 m wide.
const UAP = { cx: 236, tip: 58, crown: 69, body: 77, base: 143, hw: 10 };
// Times Tower (1999): 140 m slab, 30 m wide from here.
const TIMES = { cx: 318, top: 83, base: 153, hw: 7 };
// KICC (1973): 105 m at 11.4 km: 52 px; shaft 16 px (h/w 3.3), saucer 21 px (1.31x), roof assembly 6 px (0.12);
// 0.3° (29 px) right of Times Tower.
const KICC = { cx: 347, top: 107, base: 159, hw: 8, sw: 10 };
const BB = { x: 396, y: 112, w: 46, h: 18 };           // the Bypass billboard's face
const POLE = BB.x + 23;                                // its unipole
const LAMP = { x: 326, y: 184 };                       // the hurricane lantern on the camp table (its glass's centre)
const TABLE = { x: 321, top: 191 };                    // the camp table: left end, top row (22 px wide, legs to FEET)
const CAR = { x: 384, y: FEET };                       // the Land Cruiser: its left end and the row its tyres stand on
const ACACIA = { x: 62, top: 106, base: 199 };         // the flat-topped acacia (its trunk's foot)

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], clamp(t, 0, 1))).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
function ramp(pal, v, i, y) {                          // a dark-to-light ramp, ordered-dithered
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function flipH(src) {
  const [c, x] = makeCanvas(src.width, src.height);
  x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0);
  return c;
}
// sign letters: FONT3 with wide M, W, N, K (FONT3's read as H and a Pi at 1x)
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  N: ['1001', '1101', '1011', '1001', '1001'], K: ['1001', '1010', '1100', '1010', '1001'],
};
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y), col); cx += (g?.[0].length ?? 3) + 1; }
}

/* ================= Sky: the blue hour, the afterglow low in the west (left), the city's sodium glow ================= */
const SKY = ['#050719', '#080b22', '#0b0f2c', '#0f1436', '#141a40', '#1a2049', '#212652', '#2a2c5a', '#34315e',
  '#3f3661', '#4b3b62', '#583f60', '#66445c', '#754a57', '#855152', '#97594c', '#a96347', '#bb6e44'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 175), 1.7) * 0.6;
    v += Math.pow(Math.max(0, 1 - Math.hypot((i + 40) / 380, (y - 184) / 112)), 1.2) * 0.78;   // afterglow, west
    v += Math.max(0, 1 - Math.hypot((i - 270) / 190, (y - 170) / 60)) * 0.2;                  // the city's glow
    v -= Math.max(0, (i - 380) / 100) * smooth(60, 170, y) * 0.08;                              // cooler to the north
    return v;
  });
}
function renderMoon() {                                 // a young moon after sunset: lit along its bottom, lying on its back
  const r = MOON.r, s = r * 2 + 3, p = painter(s, s), c = r + 1;
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - c, dy = y - c, dd = Math.hypot(dx, dy);
    if (dd > r + 0.4) continue;
    const lit = Math.hypot(dx + 0.6, dy + 3.4) > r - 0.2;                                      // the terminator
    if (lit) p.put(i, y, dd > r - 0.9 ? '#e8d6a8' : dy > r - 3 ? '#fff4d4' : '#f6e6bc');
    else p.put(i, y, '#1c2048');                                                                // earthshine
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(254);
  while (STARS.length < 120) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 100);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 14 || (x < 190 && y > 70)) continue;
    STARS.push({ x, y, k: 10 + Math.floor(r() * 40), ph: r(), b: y < 50 ? 0.5 + r() * 0.45 : 0.25 + r() * 0.3, big: r() < 0.06 });
  }
}
// long thin streaks of cloud, dithered into the sky with no outline, tileable so they drift; each is baked twice, lit
// rose from below by the afterglow and cool, a step lighter than the sky round it, and drawn warm in the west and cool
// in the north (drawSky)
const WISPS = [
  { seed: 5, y: 4, h: 11, k: 1, warm: ['#1a1a44', '#22224e', '#2a2656'], cool: ['#121842', '#181e4c', '#1e2454'] },
  { seed: 47, y: 31, h: 11, k: 2, warm: ['#262250', '#2e2858', '#383060'], cool: ['#1a2050', '#20265a', '#283064'] },
  { seed: 19, y: 56, h: 11, k: 1, warm: ['#3a3260', '#463a6a', '#544274'], cool: ['#262c5e', '#30366a', '#3c4276'] },
  { seed: 9, y: 86, h: 10, k: 2, warm: ['#4e3c64', '#704c68', '#96605e'], cool: ['#30346a', '#3a3e74', '#46487e'] },
  { seed: 31, y: 108, h: 10, k: 1, warm: ['#5e446a', '#84546a', '#ac665e'], cool: ['#383a6a', '#424474', '#4e4e7e'] },
];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < 5; b++) {
    const cx = r() * W, len = 60 + r() * 90;
    for (let k = 0; k < 6; k++) blobs.push({ x: cx + (r() - 0.5) * len, y: h * (0.3 + r() * 0.4), rx: 10 + r() * 20, ry: 1.1 + r() * 1.5 });
  }
  const warm = painter(W, h), cool = painter(W, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) {
      let dx = x - b.x; dx -= Math.round(dx / W) * W;
      v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry));
    }
    if (v > 0.25 && (v - 0.25) * 2.2 > bayer(x, y)) {
      warm.put(x, y, ramp(def.warm, 0.3 + v * 0.7 - (y < h / 2 ? 0.25 : 0), x, y));
      cool.put(x, y, ramp(def.cool, 0.3 + v * 0.7, x, y));
    }
  }
  return { warm: warm.done(), cool: cool.done(), y: def.y, k: def.k };
}
// a low mist off the wet plain after the rain, drifting over the waterhole and round the giraffes' legs (two layers)
const MISTS = [{ seed: 3, y: 176, h: 30, k: 1, a: 0.34 }, { seed: 8, y: 182, h: 24, k: -2, a: 0.26 }];
function renderMist(def) {
  const p = painter(W, def.h), r = rng(def.seed), bands = [];
  for (let k = 0; k < 5; k++) bands.push({ x: r() * W, rx: 40 + r() * 70, y: def.h * (0.35 + r() * 0.35), ry: 3 + r() * 4 });
  for (let y = 0; y < def.h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of bands) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx / b.rx) ** 2 - ((y - b.y) / b.ry) ** 2); }
    v += 0.15 * Math.sin(TAU * 3 * x / W + y * 0.3 + def.seed);
    if (v > 0 && v * 1.6 > bayer(x, y)) p.put(x, y, y < def.h * 0.4 ? '#5a5678' : '#6e6688');
  }
  return { c: p.done(), y: def.y, k: def.k, a: def.a };
}

/* ================= The city: hazy far blocks, Upper Hill and the CBD, the lit edge along the park ================= */
// Night facades against the glowing sky: violet-blue bodies a step apart from their neighbours, a lighter coping where
// the glow catches the roofline, warm (sodium) and cool (office) windows. Nearer bands are darker and more lit.
const NIGHT = '#0d1030', GLOWC = '#ffb878';
const WARMW = ['#ffd890', '#f2b866', '#d8904a'], COOLW = ['#e8f0ff', '#b8c8e8', '#8898c0'];
const WINS = [];                                        // windows that go on and off (drawn per frame)
// [x0, x1, top, base, shade 0..3, haze 0..1, kind]
const FAR_BLOCKS = [
  [16, 34, 152, 172, 1, 0.8], [34, 52, 148, 172, 2, 0.8], [92, 104, 128, 172, 1, 0.75], [104, 118, 122, 172, 2, 0.75],
  [118, 128, 132, 172, 0, 0.75], [168, 182, 114, 172, 1, 0.7], [182, 196, 122, 172, 3, 0.7], [246, 254, 106, 172, 2, 0.72],
  [256, 265, 94, 172, 1, 0.8, 'far'], [270, 280, 114, 172, 0, 0.72], [282, 291, 102, 172, 2, 0.78, 'far'], [292, 302, 108, 172, 1, 0.7],
  [362, 374, 122, 172, 3, 0.72], [374, 388, 128, 172, 1, 0.72], [440, 458, 136, 172, 2, 0.78],
];
const MID_BLOCKS = [                                    // Upper Hill's offices round the towers, the CBD's lesser blocks
  [60, 74, 152, 176, 1, 0.34], [74, 88, 147, 176, 3, 0.32], [88, 100, 141, 176, 2, 0.3], [100, 112, 132, 176, 1, 0.3],
  [112, 122, 126, 176, 3, 0.3], [122, 145, 104, 176, 0, 0.3, 'slab'], [160, 172, 118, 176, 2, 0.28], [172, 184, 110, 176, 1, 0.24],
  [184, 198, 121, 176, 3, 0.3], [198, 212, 113, 176, 2, 0.26], [212, 225, 124, 176, 0, 0.3], [248, 260, 118, 176, 3, 0.28],
  [260, 272, 126, 176, 1, 0.3], [272, 286, 114, 176, 2, 0.26], [286, 300, 104, 176, 0, 0.22, 'tele'], [300, 310, 121, 176, 3, 0.28],
  [327, 336, 128, 176, 1, 0.3], [336, 358, 139, 176, 2, 0.3], [358, 368, 133, 176, 0, 0.3], [368, 384, 137, 176, 1, 0.3],
];
const BODY = ['#141634', '#1b1c3c', '#222244', '#2a284c', '#33304f'];
function drawBlock(p, [x0, x1, top, base, shade, haze, kind], r) {
  const pal = BODY.map(c => mixHex(c, '#3a3460', haze * 0.85));
  const lit = WARMW.map(c => mixHex(c, '#6a5a78', haze)), cool = COOLW.map(c => mixHex(c, '#5a5a80', haze));
  const pale = kind === 'slab';                         // a pale office slab in front of Britam's foot, as in every photo
  for (let y = top; y < base; y++) for (let i = x0; i < x1; i++) {
    let col = pale ? ramp(['#26263e', '#30304a', '#3a3a54'], 0.6 - (y - top) / (base - top) * 0.5, i, y) : pal[Math.min(4, shade + (i === x0 ? 1 : 0))];
    if (y === top) col = pale ? '#5a5870' : mixHex(pal[4], GLOWC, 0.18 * (1 - haze));
    if (i === x1 - 1 && !pale) col = pal[Math.max(0, shade - 1)];
    p.put(i, y, col);
  }
  const fy = kind === 'far' ? 3 : pale ? 3 : 4, fx = pale ? 2 : 3;
  for (let y = top + 3; y < base - 2; y += fy) for (let i = x0 + 2; i < x1 - 1; i += fx) {
    const v = r();
    if (pale) { p.put(i, y, v < 0.5 ? cool[1] : v < 0.7 ? lit[1] : '#2a2a44'); continue; }
    if (v < 0.24) p.put(i, y, lit[Math.floor(r() * 2)]);
    else if (v < 0.36) p.put(i, y, cool[1 + Math.floor(r() * 2)]);
    else if (v < 0.52 && haze < 0.5) WINS.push({ x: i, y, on: r() < 0.6 ? lit[0] : cool[1], off: pal[shade], k: 6 + Math.floor(r() * 4) * 2, s: r() });
  }
  if (kind === 'far' || kind === 'tele') { p.put((x0 + x1) >> 1, top - 1, '#3a3456'); p.put((x0 + x1) >> 1, top - 2, '#3a3456'); }
}
function renderFarCity() {
  const p = painter(W, H), r = rng(77);
  FAR_BLOCKS.forEach(b => drawBlock(p, b, r));
  return p.done();
}
function renderMidCity() {
  const p = painter(W, H), r = rng(78);
  MID_BLOCKS.forEach(b => drawBlock(p, b, r));
  return p.done();
}
// The city's edge along the park: Nairobi West, South B and C and the Industrial Area, low flats and sheds, street
// lights, trees; then the park's boundary trees. A dark band with many small lights, its top ragged.
function renderEdge() {
  const p = painter(W, H), r = rng(91);
  let x = 0;
  while (x < W) {
    const w = 6 + Math.floor(r() * 12), top = 156 + Math.floor(r() * 10) - (x > 100 && x < 380 ? 4 : 0), shade = Math.floor(r() * 3);
    for (let y = top; y < 178; y++) for (let i = x; i < x + w; i++) {
      let col = ['#101230', '#14163a', '#191a40'][shade];
      if (y === top) col = '#2a2850';
      p.put(i, y, col);
    }
    for (let y = top + 2; y < 174; y += 3) for (let i = x + 1; i < x + w - 1; i += 2) {
      const v = r();
      if (v < 0.2) p.put(i, y, WARMW[1 + Math.floor(r() * 2)]);
      else if (v < 0.27) p.put(i, y, COOLW[1]);
    }
    x += w;
  }
  for (let k = 0; k < 70; k++) {                        // street lights: sodium dots along the roads
    const i = Math.floor(r() * W), y = 166 + Math.floor(r() * 9);
    p.put(i, y, k % 5 ? '#ffc070' : '#fff0d0');
  }
  for (let i = 0; i < W; i++) {                         // the park's boundary trees and the Bypass embankment
    const h = 4 + Math.round(3 * Math.sin(i * 0.21) + 2 * Math.sin(i * 0.57 + 1) + hash(i, 5) * 2);
    for (let y = 178 - h; y < VIA + 2; y++) p.put(i, y, (y - (178 - h)) < 1 && hash(i, y) < 0.5 ? '#1e1c3c' : '#0c0e24');
  }
  return p.done();
}

/* ================= Britam Tower: the prism slab of blue glass, its offices lit, the blue-lit mast ================= */
// From the park its south face is a slab of blue glass barely narrowing upward, cut by a big Λ: the two creases where
// the faces fold towards the two-sided roof run from the mast's foot down to the slab's edges about two thirds of the
// way down; LED dots run along them (its night look since 2018). The inner facet faces the city and catches its glow.
// The mast is a lattice spindle (the helical wind turbines) on a pole, lit blue, a spike and a red beacon on top.
const GLASS = ['#1e2448', '#262e54', '#2f3960', '#38446c', '#434f7a', '#505d88', '#5e6b94'];
const BRIT_DOTS = [];                                   // the LED dots along the creases (drawn per frame)
const britHW = y => Math.round(lerp(BRIT.hw - 1, BRIT.hw, (y - BRIT.roof) / (BRIT.base - BRIT.roof)));   // 16 px at the roof, 18 low down
function renderBritam() {
  const p = painter(W, H), { cx, roof, base } = BRIT, foldY = roof + 48;
  for (let y = roof; y < base; y++) {
    const hw = britHW(y), x0 = cx - hw, x1 = cx + hw - 1;
    const spread = clamp((y - roof) / (foldY - roof), 0, 1) * (hw - 0.5);   // where the creases are on this row
    const cl = Math.round(cx - 0.5 - spread), cr = Math.round(cx - 0.5 + spread), fl = (y - roof) % 3;
    for (let i = x0; i <= x1; i++) {
      const u = (i - x0) / (x1 - x0), inner = i > cl && i < cr && y < foldY + 2;
      let v = inner ? 0.86 - u * 0.12 : 0.56 - u * 0.4;
      v -= (y - roof) / (base - roof) * 0.12;
      let col = ramp(GLASS, fl === 0 ? v - 0.16 : v, i, y);
      if (fl !== 0 && i > x0 && i < x1 && (i - x0) % 2 === 1) {                  // office windows behind the glass
        const h = hash(i, y >> 1, 41 + fl);
        if (h < 0.16) col = h < 0.11 ? (inner ? '#c8d0ec' : '#a8b4dc') : '#e8c88a';
      }
      if (i === x0) col = '#7a84ae';                    // the west edge, lit by the afterglow
      else if (i === x1) col = GLASS[0];
      p.put(i, y, col);
    }
    if (y < foldY) BRIT_DOTS.push({ x: cl, y }, { x: cr, y });
  }
  const rw = britHW(roof);
  for (let i = cx - rw; i < cx + rw; i++) { p.put(i, roof - 1, '#6a74a0'); p.put(i, roof - 2, (i - cx) % 3 ? '#3a4470' : null); }
  // the mast: a lattice spindle on a pole, lit blue
  for (let y = BRIT.tip; y < roof - 1; y++) {
    const k = y - BRIT.tip;
    let hw = 0;
    if (k >= 8 && k <= 21) hw = Math.round(Math.sin(Math.PI * (k - 8) / 13) * 3);
    if (y >= roof - 5) hw = 1;
    for (let dx = -hw; dx <= hw; dx++) {
      const edge = Math.abs(dx) === hw;
      if (hw >= 2 && !edge && (dx + k) % 2) continue;    // the lattice: open between its members
      p.put(cx + dx, y, edge ? '#5a7ac0' : '#3a5494');
    }
    p.put(cx, y, '#86a6e4');
  }
  return p.done();
}

/* ================= UAP Old Mutual Tower: setbacks, the hipped crown lit lilac, the spire with its ring ================= */
const UAP_DOTS = [];
function renderUAP() {
  const p = painter(W, H), { cx, tip, crown, body, base } = UAP;
  const stages = [[body, body + 7, 7], [body + 7, body + 25, 9], [body + 25, base, 10]];   // attic, upper body, lower body
  for (const [y0, y1, hw] of stages) for (let y = y0; y < y1; y++) for (let i = cx - hw; i < cx + hw + 1; i++) {
    const u = (i - cx + hw) / (2 * hw);
    let col = ramp(['#26243e', '#302d48', '#3c3852', '#4a455c'], 0.75 - u * 0.6 + ((i - cx) % 4 === 0 ? 0.1 : 0), i, y);
    if (y === y0) col = '#6a6478';
    if (i === cx - hw) col = '#5a5468';
    p.put(i, y, col);
    if (y > y0 + 1 && (y - y0) % 3 === 1 && (i - cx) % 2 === 0 && i > cx - hw && i < cx + hw) UAP_DOTS.push({ x: i, y, s: hash(i, y, 7) });
  }
  for (let y = crown; y < body; y++) {                  // the hipped pyramid crown
    const hw = Math.round(1 + (y - crown) / (body - crown - 1) * 6);
    for (let i = cx - hw; i <= cx + hw; i++) p.put(i, y, i <= cx ? (i === cx - hw ? '#f4e8f8' : '#d8c4e4') : (i === cx + hw ? '#8a7aa0' : '#b8a2c8'));
  }
  for (let y = tip; y < crown; y++) { p.put(cx, y, y < tip + 3 ? '#c8c0d0' : '#a49cb0'); if (y > crown - 4) p.put(cx - 1, y, '#8a8298'); }
  return p.done();
}

/* ================= Times Tower: a pale slab of offices with a mast on its roof ================= */
function renderTimes() {
  const p = painter(W, H), { cx, top, base, hw } = TIMES, r = rng(140);
  for (let y = top; y < base; y++) for (let i = cx - hw; i < cx + hw; i++) {
    const u = (i - cx + hw) / (2 * hw);
    let col = ramp(['#2a2a46', '#36364f', '#44425a', '#525066'], 0.8 - u * 0.6, i, y);
    if (y === top) col = '#7a7690';
    if ((i - cx + hw) % 3 === 0) col = mixHex(col, '#5a586e', 0.4);                // the vertical mullion bands
    p.put(i, y, col);
  }
  for (let y = top + 3; y < base - 2; y += 2) for (let i = cx - hw + 1; i < cx + hw - 1; i++) {
    if ((i - cx + hw) % 3 === 0) continue;
    const v = r();
    if (v < 0.3) p.put(i, y, COOLW[v < 0.12 ? 0 : 1]); else if (v < 0.36) WINS.push({ x: i, y, on: COOLW[0], off: '#36364f', k: 8, s: r() });
  }
  p.fill(cx - 3, top - 3, 6, 3, '#3a3852'); p.fill(cx - 3, top - 3, 6, 1, '#5a5670');
  for (let y = top - 11; y < top - 3; y++) p.put(cx + 1, y, '#4a4660');
  return p.done();
}

/* ================= KICC: the terracotta cylinder, its window bands and the saucer on top ================= */
// rows: the saucer (the helipad) 106-107, its neck 108-109, then the shaft, floodlit warm from the city below, with
// recessed bands of windows every three rows between terracotta spandrels and fins; its lower half is hidden by the
// Industrial Area's roofs from here.
const TERRA = ['#2e1612', '#4a2218', '#6c3320', '#90472a', '#b25e36', '#cc7644', '#e09258'];
function renderKICC() {
  const p = painter(W, H), { cx, top, base, hw, sw } = KICC;
  for (let i = cx - sw; i <= cx + sw; i++) {           // the saucer: a lit top, its rim, its dark underside
    const u = (i - cx + sw) / (2 * sw), ad = Math.abs(i - cx);
    if (ad < sw) p.put(i, top, u < 0.45 ? '#f8d0a0' : u < 0.8 ? '#e0aa7a' : '#a8704c');
    p.put(i, top + 1, u < 0.5 ? '#d89468' : '#9a6040');
    if (ad < sw - 1) p.put(i, top + 2, ad > sw - 4 ? '#5a3424' : '#3a2018');
  }
  for (let i = cx - 3; i <= cx + 2; i++) p.put(i, top - 1, i < cx ? '#c89a74' : '#8a6450');   // the low house on the helipad
  for (let i = cx - 5; i <= cx + 4; i++) p.put(i, top + 3, i < cx - 2 ? TERRA[3] : i < cx + 2 ? TERRA[2] : TERRA[1]);   // its neck
  for (let y = top + 4; y < base; y++) {
    const band = (y - top - 4) % 3;
    for (let i = cx - hw; i < cx + hw; i++) {
      const n = (i + 0.5 - cx) / hw, v = Math.sqrt(Math.max(0, 1 - n * n)) * 0.7 - n * 0.18 + 0.14;
      let col;
      if (band === 0) col = ramp(TERRA, v + 0.16, i, y);                          // the floor's spandrel, floodlit
      else if ((i - cx) % 2 === 0) col = ramp(TERRA, v - 0.12, i, y);             // the fins between the windows
      else col = hash(i, y >> 1, 105) < 0.72 && Math.abs(n) < 0.9 ? (v > 0.45 ? WARMW[0] : WARMW[1]) : ramp(TERRA, v - 0.3, i, y);
      p.put(i, y, col);
    }
  }
  return p.done();
}

/* ================= The SGR viaduct, the far plain, the waterhole's banks and the near grass ================= */
// The viaduct (2018) carries the Standard Gauge Railway to Naivasha across the park on piers about 8 m high; at 1.6 km
// its deck is a long dark beam just under the horizon, the far plain showing between its piers.
const PIERS = Array.from({ length: 14 }, (_, k) => 14 + k * 37);
function renderViaduct() {
  const p = painter(W, H);
  for (const x of PIERS) for (let y = VIA + 4; y < PLAIN + 6; y++) for (let i = x - 1; i <= x + 1; i++)
    p.put(i, y, i === x - 1 ? '#2a2a46' : i === x + 1 ? '#0e0e22' : '#1c1c36');
  for (let i = 0; i < W; i++) {
    p.put(i, VIA - 1, (i & 1) ? '#24243e' : null);                                   // the noise barrier's posts
    p.put(i, VIA, '#3e3a5c'); p.put(i, VIA + 1, '#22223c'); p.put(i, VIA + 2, '#1a1a32'); p.put(i, VIA + 3, '#121228');
  }
  return p.done();
}
const POOL_SHAPE = (i, y) => {                         // inside the waterhole? an irregular pan, wider than deep
  const cx = (POOL.x0 + POOL.x1) / 2, rx = (POOL.x1 - POOL.x0) / 2;
  const u = (i - cx) / rx, wob = 1.4 * Math.sin(i * 0.11) + 1.1 * Math.sin(i * 0.29 + 2);
  const top = POOL.top + 2 + u * u * 8 + wob * 0.5, bot = POOL.bot - 1 - u * u * 9 + wob;
  return Math.abs(u) < 1 && y >= top && y <= bot;
};
function renderPlain() {                               // the far plain, under the viaduct, from the park's edge to the pan
  return ditherFill(W, POOL.top + 4 - VIA, ['#141628', '#1a1c30', '#212238', '#282840'], (i, j) => 0.2 + j / 20 * 0.65 + (hash(i >> 2, j, 3) - 0.5) * 0.25);
}
function renderNear() {
  const p = painter(W, H), r = rng(207);
  const camp = (i, y) => Math.max(0, 1 - Math.hypot((i - 370) / 130, (y - FEET - 4) / 24));   // trampled ground round the stop
  for (let y = POOL.top; y < H; y++) for (let i = 0; i < W; i++) {
    if (POOL_SHAPE(i, y)) continue;
    const d = (y - POOL.top) / (H - POOL.top);
    let v = 0.6 - d * 0.28 + (bayer(i, y) - 0.5) * 0.12 + (hash(i >> 1, y >> 1, 9) - 0.5) * 0.2 + camp(i, y) * 0.12;
    v += Math.max(0, 1 - Math.hypot((i - LAMP.x) / 110, (y - FEET - 6) / 30)) * 0.4;     // the lantern's warm pool
    let col = ramp(['#0b0b16', '#11111c', '#181824', '#20202c', '#2a2834', '#36303c', '#443a42'], v, i, y);
    const nearPool = [-2, -1, 1, 2, 3].some(k => POOL_SHAPE(i, y + k)) || POOL_SHAPE(i - 2, y) || POOL_SHAPE(i + 2, y);
    if (nearPool) col = y < (POOL.top + POOL.bot) / 2 ? '#3a3248' : ramp(['#2a2230', '#3a2e34', '#4a3a3a'], 0.5 + v * 0.4, i, y);   // wet mud
    if (Math.max(0, 1 - Math.hypot((i - LAMP.x) / 70, (y - FEET - 4) / 20)) > 0.2 + bayer(i, y) * 0.5) col = mixHex(col, '#9a6232', 0.42);
    p.put(i, y, col);
  }
  for (let k = 0; k < 2; k++) for (let y = FEET + 1; y < H; y++) {                    // the track's two ruts to the stop
    const u = (y - FEET - 1) / (H - FEET - 1), x = Math.round(lerp(404 + k * 52, 250 + k * 150, u * u));
    for (let i = 0; i < 2 + Math.floor(u * 5); i++) if (bayer(x + i, y) < 0.7) p.put(x + i, y, u < 0.3 ? '#2e2a36' : '#3a3440');
  }
  const bush = (bx, by, rx, ry, lit) => {              // whistling thorn: thin stems fanning up to a flat, twiggy crown
    for (let k = 0; k < 7; k++) {
      const tx = bx + (k / 6 - 0.5) * rx * 1.8, ty = by - ry + Math.abs(k - 3) * 0.6;
      const n = Math.ceil(Math.hypot(tx - bx, ty - by));
      for (let j = 0; j <= n; j++) p.put(lerp(bx, tx, j / n), lerp(by, ty, j / n), '#0a0a14');
    }
    for (let k = 0; k < rx * ry * 0.7; k++) {
      const a = r() * TAU, d = Math.sqrt(r()), i = bx + Math.cos(a) * rx * d, y = by - ry + Math.sin(a) * ry * 0.4 * d;
      p.put(i, y, y < by - ry - 1 && r() < 0.3 ? lit : '#0a0a14');
    }
  };
  for (const [bx, by, rx, ry] of [[18, 200, 12, 10], [156, 199, 8, 7], [282, 196, 7, 6], [112, 238, 17, 15], [236, 252, 12, 11], [470, 244, 14, 13]])
    bush(bx, by, rx, ry, bx < 200 ? '#5a4252' : '#3a3446');
  // grass in three depths: short tufts by the water, knee-high in the middle, tall stems along the bottom, each lit on
  // its tips from the west (left) or by the lantern
  const tuft = (x0, y0, h, n, lean) => {
    for (let k = 0; k < n; k++) {
      const x = x0 + Math.floor(r() * 3) - 1, hh = Math.max(2, Math.round(h * (0.6 + r() * 0.5))), ln = lean + (r() - 0.5) * 0.6;
      const litC = x < 170 ? '#5e4652' : Math.abs(x - LAMP.x) < 80 ? '#8a5c34' : '#34303e';
      for (let j = 0; j < hh; j++) p.put(x + Math.round(ln * j * j / hh), y0 - j, j > hh - 3 && r() < 0.5 ? litC : '#09090f');
    }
  };
  for (let i = 0; i < W; i += 5) if (!POOL_SHAPE(i, 196)) tuft(i + Math.floor(r() * 4), 200 + Math.floor(r() * 6), 4, 2, 0.2);
  return p.done();
}

/* ================= The near grass, swaying: knee-high in the middle, tall stems along the bottom ================= */
// Dark stems against the dusky ground, their seed heads lit from the west (left) or by the lantern; baked in GRASS_NF
// sway frames for two groups that the wind moves out of step (drawGrass).
const GRASS_NF = 7, BLADES = [];
{
  const r = rng(219);
  for (let i = 0; i < W; i += 3) for (let k = 0; k < 2; k++) BLADES.push({ x: i + Math.floor(r() * 3), y: 210 + Math.floor(r() * 22), h: 5 + Math.floor(r() * 7), lean: (r() - 0.5) * 0.5, g: (k + (i >> 4)) & 1, amp: 1.4 });
  for (let i = 0; i < W; i += 2) if (Math.sin(i * 0.05) + Math.sin(i * 0.17 + 1) > -1.2)
    for (let k = 0; k < 2; k++) BLADES.push({ x: i + Math.floor(r() * 3), y: H - Math.floor(r() * 18), h: 12 + Math.floor(r() * 13), lean: (r() - 0.3) * 0.5, g: (k + (i >> 3)) & 1, amp: 2.8 });
  BLADES.forEach(b => { b.lit = b.x < 170 ? '#76586a' : Math.abs(b.x - LAMP.x) < 90 ? '#a87040' : '#4a4458'; b.tip = r() < 0.75; });
}
function renderGrass(g, f) {
  const p = painter(W, H - 200), sw = lerp(-1, 1, f / (GRASS_NF - 1));
  for (const b of BLADES) {
    if (b.g !== g) continue;
    for (let j = 0; j < b.h; j++) {
      const u = j / b.h, x = b.x + b.lean * j + sw * b.amp * u * u;
      p.put(x, b.y - j - 200, j > b.h - 3 && b.tip ? b.lit : '#07070d');
    }
  }
  return p.done();
}

/* ================= The flat-topped acacia (an umbrella thorn) the near giraffe browses ================= */
function renderAcacia() {
  const p = painter(W, H), { x, top, base } = ACACIA;
  const limb = (x0, y0, x1, y1, w) => {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let k = 0; k <= n; k++) { const xx = lerp(x0, x1, k / n), yy = lerp(y0, y1, k / n), ww = Math.max(1, Math.round(lerp(w, 1, k / n))); for (let j = 0; j < ww; j++) p.put(xx + j - (ww >> 1), yy, j === 0 ? '#3a2a3a' : '#0c0a16'); }
  };
  limb(x, base, x - 2, base - 42, 4);
  limb(x - 2, base - 42, x - 30, top + 12, 3); limb(x - 2, base - 42, x + 8, top + 10, 3); limb(x - 1, base - 50, x + 34, top + 13, 3);
  limb(x - 16, top + 28, x - 44, top + 14, 1); limb(x + 16, top + 22, x + 44, top + 15, 2);
  const tiers = [[x - 4, top + 4, 34, 4], [x + 2, top + 10, 52, 4.5], [x - 26, top + 13, 22, 3], [x + 34, top + 13, 22, 3]];
  for (const [cx, cy, rx, ry] of tiers) for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let i = Math.floor(cx - rx); i <= cx + rx; i++) {
    const v = 1 - ((i - cx) / rx) ** 2 - ((y - cy) / ry) ** 2 + (hash(i, y, 31) - 0.5) * 0.7;
    if (v <= 0) continue;
    const topEdge = !(1 - ((i - cx) / rx) ** 2 - ((y - 1 - cy) / ry) ** 2 > 0.2);
    p.put(i, y, topEdge && hash(i, y, 32) < 0.7 ? (i < x ? '#6a4858' : '#4a3a50') : y > cy + ry * 0.4 ? '#08080f' : (hash(i, y, 34) < 0.15 ? '#22202e' : '#11101c'));
  }
  return p.done();
}

/* ================= Maasai giraffes ================= */
// Built in metres from a side view of a bull about 5 m to the ossicones: legs 1.9 m to the belly, the back sloping from
// the withers (3.0 m) to the rump (2.5 m), a 2 m neck, a 0.6 m head; jagged dark patches with pale lines between them
// (the Maasai giraffe's), cream lower legs. Lit by the afterglow from the west (left): a warm rim on the left edges.
// Poses: standing, browsing (the neck stretched up into the canopy), drinking (the forelegs splayed, the head down).
const POSES = {
  stand: { d: 0, head: [1.7, 4.8], muzzle: [2.25, 4.5], fl: [0.8, 1.0], splay: 0 },
  browse: { d: 0, head: [1.4, 5.3], muzzle: [1.85, 5.6], fl: [0.8, 1.0], splay: 0 },
  drink: { d: 0.75, head: [2.25, 0.62], muzzle: [2.5, 0.12], fl: [0.15, 1.8], splay: 1 },
};
const GIR = { tan: '#9a6a3e', patch: '#4a2614', line: '#8a5e38', leg: '#b8a080', mane: '#2e1a10', rim: '#c88a78', eye: '#140c0a' };
function buildGiraffe(s, pose, dir, seed, dark) {
  const P = typeof pose === 'string' ? POSES[pose] : pose, d = P.d;
  const w = Math.ceil(4.6 * s) + 4, h = Math.ceil(6.0 * s) + 3, ox = Math.round(w / 2 - dir * 0.6 * s), oy = h - 1;
  const mask = new Uint8Array(w * h);
  const X = ux => ox + dir * ux * s, Y = uy => oy - uy * s;
  const set = (i, y, id) => { i = Math.round(i); y = Math.round(y); if (i >= 0 && y >= 0 && i < w && y < h && (!mask[y * w + i] || id < 4)) mask[y * w + i] = id; };
  const poly = (pts, id) => {                           // scanline fill of a convex polygon given in metres
    const q = pts.map(([a, b]) => [X(a), Y(b)]), y0 = Math.floor(Math.min(...q.map(v => v[1]))), y1 = Math.ceil(Math.max(...q.map(v => v[1])));
    for (let y = y0; y <= y1; y++) {
      const xs = [];
      for (let k = 0; k < q.length; k++) {
        const [ax, ay] = q[k], [bx, by] = q[(k + 1) % q.length];
        if ((ay <= y + 0.5 && by > y + 0.5) || (by <= y + 0.5 && ay > y + 0.5)) xs.push(ax + (y + 0.5 - ay) / (by - ay) * (bx - ax));
      }
      if (xs.length < 2) continue;
      for (let i = Math.round(Math.min(...xs)); i < Math.round(Math.max(...xs)); i++) set(i, y, id);
    }
  };
  const line = (a, b, w0, w1, id) => {                  // a tapering limb from a to b (metres), widths in metres
    const n = Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) * s * 2) + 1;
    for (let k = 0; k <= n; k++) {
      const u = k / n, cx = X(lerp(a[0], b[0], u)), cy = Y(lerp(a[1], b[1], u)), ww = Math.max(1, lerp(w0, w1, u) * s);
      for (let j = 0; j < Math.round(ww); j++) set(cx - ww / 2 + j + 0.5, cy, id);
    }
  };
  // the body: a deep chest under high withers, the back sloping to a narrow rump
  poly([[-1.25, 2.1], [-1.2, 2.45], [-0.3, 2.68 - d * 0.3], [0.62, 3.05 - d], [1.2, 2.72 - d], [1.32, 2.05 - d], [1.05, 1.6 - d],
    [0.2, 1.68 - d * 0.5], [-0.9, 1.78]], 1);
  const nb = [1.02, 2.5 - d];
  line(nb, P.head, 0.82, 0.3, 2);                                                         // the neck, thick at the chest
  line([nb[0] - 0.32, nb[1] + 0.4], [P.head[0] - 0.1, P.head[1] + 0.04], 0.12, 0.08, 6);   // the short dark mane
  line(P.head, P.muzzle, 0.34, 0.2, 3);
  const up = P.up ?? (pose === 'drink' ? [-0.08, -0.2] : [-0.12, 0.24]);
  line(P.head, [P.head[0] + up[0], P.head[1] + up[1]], 0.12, 0.1, 6);                      // the ossicones
  if (P.splay) {
    line([0.72, 1.95 - d], [P.fl[0], 0], 0.24, 0.13, 4); line([1.05, 1.9 - d], [P.fl[1], 0], 0.24, 0.13, 4);
  } else for (const fx of P.fl) { line([fx, 1.9], [fx - 0.02, 0.95], 0.26, 0.17, 4); line([fx - 0.02, 0.95], [fx, 0], 0.15, 0.13, 5); }
  for (const hx of [-0.78, -1.0]) { line([hx, 2.1], [hx - 0.2, 1.0], 0.36, 0.17, 4); line([hx - 0.2, 1.0], [hx - 0.08, 0], 0.15, 0.13, 5); }
  // solid silhouettes against the glow: warm-brown patches and tan lines, dim; the side towards the lantern (right)
  // catches its warm light over a few pixels, the west (left) edges a rose rim of afterglow
  const pal = {}, LIT = { patch: '#a85a2c', line: '#f0b878', leg: '#f0d8b0', tan: '#e8b070', mane: '#7a4426' };
  for (const k in GIR) pal[k] = mixHex(GIR[k], NIGHT, k === 'rim' ? dark * 0.45 : dark);
  const p = painter(w, h);
  const at = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : mask[y * w + i];
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const id = mask[y * w + i];
    if (!id) continue;
    const ux = (i - ox) / s * dir, uy = (oy - y) / s;
    let key = 'tan';
    if (id === 6) key = 'mane';
    else if (id === 5) key = 'leg';
    else if (id !== 3) {                                // the patch network: big on the body, smaller up the neck and down the legs
      const cell = id === 1 ? 0.62 : id === 2 ? 0.44 - Math.max(0, uy - 3) * 0.04 : 0.32;
      const gx = Math.floor(ux / cell), gy = Math.floor(uy / cell);
      let d1 = 9, d2 = 9;
      for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) {
        const px = (gx + a + 0.1 + hash(seed + id, gx + a, gy + b) * 0.8) * cell, py = (gy + b + 0.1 + hash(seed + 1 + id, gx + a, gy + b) * 0.8) * cell;
        const dd = Math.hypot(ux - px, uy - py);
        if (dd < d1) { d2 = d1; d1 = dd; } else if (dd < d2) d2 = dd;
      }
      key = d2 - d1 < cell * 0.1 ? 'line' : 'patch';
    }
    let col = pal[key];
    let e = 0; while (e < 4 && at(i + 1 + e, y)) e++;    // how far in from the lantern-side edge
    if (e < 3 && id !== 6) col = mixHex(LIT[key] || LIT.tan, NIGHT, dark * (0.35 + e * 0.2));
    if (!at(i - 1, y) && id !== 6) col = id === 5 ? mixHex(LIT.leg, NIGHT, dark * 0.7) : pal.rim;   // the west rim, left edges only
    else if (!at(i, y - 1) && (id === 1 || id === 2) && e >= 3) col = mixHex(col, GIR.rim, 0.25);
    p.put(i, y, col);
  }
  const ex = Math.round(X(lerp(P.head[0], P.muzzle[0], 0.3))), ey = Math.round(Y(lerp(P.head[1], P.muzzle[1], 0.3)));
  p.put(ex, ey, pal.eye);
  return { c: p.done(), ox, oy, tail: [X(-1.22), Y(2.4)], s, dir };
}
// the three at the waterhole and the acacia: x, the row their feet stand on, px per metre, pose, facing
const GIRAFFES = [
  { id: 'bull', x: 88, y: 199, s: 15.2, pose: 'browse', dir: 1, dark: 0.42 },
  { id: 'cow', x: 190, y: 196, s: 12.8, pose: 'stand', dir: 1, dark: 0.46 },
  { id: 'calf', x: 234, y: 196, s: 9.0, pose: 'stand', dir: -1, dark: 0.46 },
];

/* ================= The sundowner stop: the Land Cruiser, the camp table, the lantern ================= */
// A night game drive (they leave the main gate with a ranger) has pulled off the track by the waterhole for chai: a
// sand-coloured Land Cruiser facing the giraffes with its roof popped up, a folding table with a hurricane lantern,
// enamel mugs and a plate of mandazi, two canvas chairs. Lit warm from the lantern, on the left of the car.
const CAR_W = 96, CAR_H = 58;                           // a 70-series game-drive Land Cruiser: 4.6 m long, 2 m to the roof, the pop-top up 0.5 m
function renderCar() {
  const p = painter(CAR_W, CAR_H), K = '#0a0a12', G = 57;    // G: the row its tyres stand on
  // sand paint lit from the lantern on its front (left), falling off towards the back
  const paint = (i, y, d = 0) => ramp(['#2a241c', '#3e3628', '#584c36', '#766648', '#968258', '#b49c6c', '#ccb47e'], 0.96 - i / 105 - d, i, y);
  for (let y = 15; y < 45; y++) for (let i = 3; i < 92; i++) {                     // bonnet, cab and the back, a tall box
    if (i < 25 && y < 26) continue;
    if (i >= 25 && i < 28 && y < 26 - (i - 25) * 4) continue;                     // the near-upright windscreen
    let col = paint(i, y, y > 33 ? 0.2 : 0);
    if (y === 15 || (i < 25 && y === 26)) col = '#e0c890';                        // roof edge, bonnet edge
    if (y === 33) col = mixHex(col, K, 0.45);                                     // the belt line
    if (i === 3) col = '#e8d098';
    if ((i === 44 || i === 62) && y > 16 && y < 44) col = mixHex(paint(i, y), K, 0.55);   // door shut lines
    p.put(i, y, col);
  }
  for (const [x0, x1] of [[29, 41], [47, 59], [65, 88]]) for (let y = 17; y < 26; y++) for (let i = x0; i <= x1; i++)   // open windows, unequal
    p.put(i, y, y === 17 ? '#24242e' : i === x0 ? '#4a4038' : (i - x0 < 6 ? '#3a3240' : '#17151c'));
  p.fill(76, 17, 1, 9, '#6a5c42');                                                 // the rear window's pillar
  for (let y = 15; y < 26; y++) { const i = 25 + Math.round((26 - y) / 4); p.put(i, y, '#7a7088'); p.put(i + 1, y, '#3a3a4a'); }   // windscreen
  p.fill(24, 21, 2, 3, '#1a1a20');                                                 // mirror
  for (let i = 27; i < 92; i++) { p.put(i, 1, '#d8c494'); p.put(i, 2, '#a08c62'); p.put(i, 3, '#5a4c36'); p.put(i, 4, i % 3 ? '#2a2620' : null); }   // the raised roof, its rolled canvas
  for (const sx of [30, 88]) for (let y = 4; y < 15; y++) p.put(sx, y, y < 6 ? '#5a5240' : '#9a8a64');           // struts (the gap stays open)
  p.fill(34, 0, 4, 1, '#2a2a34'); p.put(34, 0, '#a8a8b8');                         // the roof spotlight
  for (let y = 11; y < 37; y++) p.fill(21, y, 2, 1, y === 11 ? '#3a3630' : '#1c1a18');   // the snorkel up the pillar
  p.fill(20, 10, 4, 2, '#24221e');                                                 // its intake
  for (let y = 24; y < 45; y++) { p.put(0, y, y % 4 ? '#1a1a20' : '#4a4a56'); p.put(1, y, '#121216'); }   // the bull bar
  p.fill(0, 30, 4, 1, '#3a3a44'); p.fill(0, 38, 4, 1, '#3a3a44'); p.fill(1, 40, 3, 3, '#2a2a30');           // its rails and winch
  p.fill(4, 29, 3, 3, '#6a6a70'); p.put(5, 30, '#c8c8c0'); p.fill(4, 33, 3, 1, '#c88a30');   // headlight (off), indicator
  for (let i = 26; i < 66; i++) p.put(i, 45, '#24221e');                           // the side step
  for (const wx of [17, 76]) for (let y = 34; y <= G; y++) for (let i = wx - 12; i <= wx + 12; i++) {   // flares, arches, chunky tyres
    const dd = Math.hypot(i - wx, y - 48), a = Math.atan2(y - 48, i - wx);
    if (dd < 9.6) p.put(i, y, dd < 2.4 ? '#7a7a82' : dd < 4.6 ? '#3e3e48' : dd < 5.4 ? '#1a1a20' : dd > 8.4 && Math.round(a * 4) % 2 ? '#2e2e36' : '#1c1c24');
    else if (dd < 11.6 && y < 48) p.put(i, y, dd < 10.6 ? K : '#24221e');
  }
  for (let y = 21; y < 42; y++) for (let i = 91; i < 96; i++) {                   // the spare wheel on the back door
    const dd = Math.abs(y - 31.5) / 10.5;
    if (dd < 1 && i > 91 + Math.round(dd * dd * 3)) p.put(i, y, i === 95 ? '#2e2e36' : (y % 3 ? '#16161c' : '#24242c'));
  }
  return p.done();
}
function renderTable() {                                // the folding table and the two canvas chairs (the lantern per frame)
  const p = painter(W, H), T = TABLE.x, y0 = TABLE.top, tw = 22;
  for (let i = T; i < T + tw; i++) { p.put(i, y0, '#e0b478'); p.put(i, y0 + 1, '#a07448'); p.put(i, y0 + 2, '#5a3e26'); }
  for (let k = 0; k < FEET - y0 - 2; k++) {
    const u = k / (FEET - y0 - 3);
    p.put(Math.round(T + 2 + u * 7), y0 + 3 + k, '#3a2a20'); p.put(Math.round(T + 9 - u * 7), y0 + 3 + k, '#2a1e18');
    p.put(Math.round(T + 12 + u * 7), y0 + 3 + k, '#3a2a20'); p.put(Math.round(T + 19 - u * 7), y0 + 3 + k, '#2a1e18');
  }
  p.fill(T + 9, y0 - 1, 7, 1, '#e8e4dc'); p.fill(T + 10, y0 - 2, 3, 1, '#ecb45a'); p.fill(T + 13, y0 - 2, 2, 1, '#c88a3a'); p.put(T + 11, y0 - 3, '#dc9c4a'); p.put(T + 14, y0 - 3, '#b8782c');   // mandazi
  for (const mx of [T + 17, T + 20]) { p.fill(mx, y0 - 4, 3, 4, '#ece8e0'); p.fill(mx, y0 - 4, 3, 1, '#3a5aa0'); }   // enamel mugs
  for (const cx of [312, 355]) {                        // canvas director's chairs facing the giraffes (left): back on the right
    for (let i = cx - 5; i <= cx + 6; i++) { p.put(i, 197, '#62743e'); p.put(i, 198, '#34421f'); }
    for (let y = 184; y < 197; y++) { p.put(cx + 6, y, '#4a3a28'); if (y < 191) { p.put(cx + 5, y, '#3e4e2a'); p.put(cx + 4, y, '#4a5c30'); } }
    for (let k = 0; k < 7; k++) { p.put(cx - 5 + k, 199 + k, '#2a2018'); p.put(cx + 5 - k, 199 + k, '#3a2a1e'); }
    p.put(cx - 5, 196, '#4a3a28'); p.put(cx + 6, 196, '#4a3a28');
  }
  return p.done();
}

/* ================= People ================= */
const SKINS = ['#4a2e20', '#6a4230', '#8a5a3c', '#b07a54', '#e0b896'], HAIRS = ['#100c0a', '#2a1a12', '#5a3a22', '#c8a060', '#b8b4b0'];
const PNIGHT = '#141634', LAMPC = '#ffc078';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.22, m = c => mixHex(c, PNIGHT, dk);
  const hh = Math.round(h * 0.17), th = Math.round(h * 0.36);
  return { h, hh, hw: Math.round(h * 0.15), th, tw: Math.round(h * 0.27), ll: h - hh - th, aw: h >= 30 ? 2 : 1,
    skin: m(SKINS[skin]), skinL: mixHex(SKINS[skin], LAMPC, 0.3), hair: m(HAIRS[hair]), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.32),
    topL: mixHex(top, LAMPC, 0.38), bot: m(bot), botL: mixHex(bot, LAMPC, 0.25), shoe: m('#1a1418'), long: o.long || 0,
    hat: o.hat ? m(o.hat) : null, beret: o.beret ? m(o.beret) : null };
}
/** one person, feet on row fy (o.sit: the seat's row), centred on x. o.view: 0 from behind, 1 facing us, 2 profile
 *  (o.dir); o.aL / o.aR: 0 down, 2 binoculars to the eyes, 3 out (pouring, holding a torch), 5 a mug at the mouth;
 *  o.lit: the side the lantern is on (-1 left, 1 right, 0 in front); o.shin: a seated person's shin, to the ground */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th, aw } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2, lit = o.lit || 0;
  const legs = o.sit ? 0 : P.ll, ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 3 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1, lw = Math.max(2, (bw - 1) >> 1);
    if (side) { R(x - 2, ly, 4, legs, P.bot); R(x + (lit > 0 ? 1 : -2), ly, 1, legs, P.botL); R(dir > 0 ? x - 2 : x - 3, fy, 5, 1, P.shoe); }
    else { R(tx, ly, bw, 3, P.bot); R(tx, ly + 3, lw, legs - 3, P.bot); R(tx + bw - lw, ly + 3, lw, legs - 3, P.bot); R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe); }
  } else if (o.sit && side) {
    const tl = Math.round(P.h * 0.24), sh = o.shin ?? 4;
    R(dir > 0 ? x - 2 : x - tl + 2, fy - 2, tl, 3, P.bot);
    R(dir > 0 ? x + tl - 4 : x - tl + 2, fy + 1, 3, sh, P.bot); R(dir > 0 ? x + tl - 4 : x - tl, fy + 1 + sh, 5, 1, P.shoe);
  } else if (o.sit) R(tx - 1, fy - 2, bw + 2, 3, P.bot);
  R(tx, ty, bw, th, P.top);                                                     // the torso, lit on the lantern's side
  if (lit > 0) R(tx + bw - 2, ty + 1, 2, th - 2, P.topL); else if (lit < 0) R(tx, ty + 1, 2, th - 2, P.topL);
  R(tx, ty, bw, 1, lit ? P.topL : P.topD);
  const hand = (hx, hy2) => R(hx, hy2, aw, aw, lit ? P.skinL : P.skin);
  const arm = (ax, st, sg) => {                         // front and back views
    if (!st) { R(ax, ty + 1, aw, th - 3, P.topD); hand(ax, ty + th - 2); }
    else if (st === 2) { R(ax, hy + 3, aw, ty - hy - 2, P.topD); R(ax + (sg < 0 ? aw : -2), hy + 2, 3, 2, '#141418'); }
    else if (st === 3) { R(sg < 0 ? ax - 3 : ax, ty + 3, 4, aw, P.topD); hand(sg < 0 ? ax - 4 : ax + 4, ty + 3); }
    else { R(ax, ty + 1, aw, 3, P.topD); hand(ax + (sg < 0 ? 1 : -1), hy + hh - 2); }
  };
  if (side) {
    const st = o.aR || 0, fx = x + dir * (bw >> 1);
    if (st === 3) { R(dir > 0 ? fx : fx - 4, ty + 3, 5, aw, P.topD); hand(fx + dir * 5, ty + 3); }
    else if (st === 2) { R(fx - (dir > 0 ? 1 : 0), ty + 1, aw, 3, P.topD); R(dir > 0 ? x + (hw >> 1) : x - (hw >> 1) - 3, hy + 2, 4, 2, '#141418'); hand(fx + dir, hy + 4); }
    else if (st === 5) { R(fx - (dir > 0 ? 1 : 0), ty + 1, aw, 3, P.topD); R(dir > 0 ? x + (hw >> 1) : x - (hw >> 1) - 2, hy + hh - 3, 3, 3, '#ece8e0'); }
    else { R(x - 1, ty + 1, aw, th - 3, P.topD); hand(x - 1, ty + th - 2); }
  } else { arm(tx - aw, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1), face = lit ? P.skinL : P.skin;
  if (view === 0) { R(hx, hy, hw, hh, P.hair); if (P.long) R(hx, hy + hh, hw, P.long, P.hair); }
  else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, 2, P.hair); if (lit) R(lit > 0 ? hx + hw - 1 : hx, hy + 2, 1, hh - 2, P.skinL);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else {
    R(hx, hy, hw, hh, face); R(hx, hy, hw, 2, P.hair); R(dir > 0 ? hx : hx + hw - 2, hy, 2, hh - 2, P.hair);   // hair over the back of the head
    R(dir > 0 ? hx + hw : hx - 1, hy + 2, 1, 1, face);                           // the nose
    if (P.long) R(dir > 0 ? hx - 1 : hx + hw - 1, hy, 2, hh + P.long, P.hair);
  }
  if (P.hat) { R(hx - 1, hy, hw + 2, 1, P.hat); R(hx, hy - 2, hw, 2, P.hat); }
  if (P.beret) { R(hx, hy - 1, hw, 2, P.beret); R(dir > 0 ? hx - 1 : hx + hw, hy, 1, 1, P.beret); }
  return hy;
}
const rk = i => frac((i + 0.5) * 0.618);
// the cast at the stop: rank 0 shows at every level above 0 (the ranger, the guide, a guest); couples share one rank
const CAST = [
  { id: 'watcher', x: 295, y: FEET, rank: 0, P: look(35, 4, 2, '#cabc94', '#4e4c3a', { hat: '#a89870' }), o: { view: 2, dir: -1, aR: 2, lit: 1 } },
  { id: 'guestA', x: 310, y: 196, rank: 0, P: look(34, 2, 0, '#3a6a8a', '#2a2a3a'), o: { sit: true, view: 2, dir: -1, aR: 5, shin: 7, lit: 1 } },
  { id: 'guide', x: 334, y: 203, rank: 0.4, P: look(34, 1, 0, '#8a2a30', '#2a2a32', { hat: '#2a2a30' }), o: { view: 1, aR: 3, lit: -1 } },
  { id: 'guestB', x: 353, y: 196, rank: 0.3, P: look(33, 4, 3, '#b0607a', '#2a2a3a', { long: 5 }), o: { sit: true, view: 2, dir: -1, aR: 3, shin: 7, lit: -1 } },
  { id: 'ranger', x: 373, y: FEET, rank: 0, P: look(36, 0, 0, '#4e5e36', '#3e4c2c', { beret: '#24341e' }), o: { view: 2, dir: -1, aR: 3, lit: -1 } },
];

/* ================= The billboard: a mega-board on its pole by the Southern Bypass, at the park's edge ================= */
// The city's own ad: "MANDAZI NA CHAI" (mandazi with tea, an evening's snack), an enamel mug and two mandazi, on red;
// lit by three lamps on arms, night-graded so it stays under the towers.
const BB_LAMPS = [BB.x + 8, BB.x + 23, BB.x + 38];
function renderBillboard() {
  const p = painter(W, H), K = '#0c0c18';
  for (let y = BB.y + BB.h + 2; y < 172; y++) { p.put(POLE - 1, y, '#2a2a40'); p.put(POLE, y, '#1a1a2c'); p.put(POLE + 1, y, '#101020'); }
  for (let i = BB.x - 1; i <= BB.x + BB.w; i++) { p.put(i, BB.y - 1, '#2a2638'); p.put(i, BB.y + BB.h, K); p.put(i, BB.y + BB.h + 1, i % 2 ? '#2a2a3c' : K); }
  for (let y = BB.y - 1; y <= BB.y + BB.h; y++) { p.put(BB.x - 1, y, '#2a2638'); p.put(BB.x + BB.w, y, K); }
  for (const lx of BB_LAMPS) { for (let y = BB.y - 4; y < BB.y - 1; y++) p.put(lx, y, '#1a1a28'); p.put(lx - 1, BB.y - 5, K); p.put(lx, BB.y - 5, K); p.put(lx + 1, BB.y - 5, K); }
  return p.done();
}
function renderAd() {
  const { w, h } = BB;
  const c = ditherFill(w, h, ['#8a1e18', '#a82a20', '#c43a28', '#d85a34'], (i, j) => 0.55 + 0.4 * Math.max(...BB_LAMPS.map(lx => Math.exp(-(((i - (lx - BB.x)) / 7) ** 2)) * Math.max(0, 1 - j / 14))));
  const x = c.getContext('2d');
  const MUG = ['.......', '.BBBBB.', 'WWWWWWW', 'WWWWWWWW', 'WWWWWW.W', 'WWWWWWWW', 'WWWWWW..', '.WWWW...'];
  MUG.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') { x.fillStyle = row[i] === 'B' ? '#2a4a9a' : '#f4f0e6'; x.fillRect(2 + i, 8 + j, 1, 1); } });
  x.fillStyle = '#6a3a1a'; x.fillRect(3, 10, 5, 1);                                     // the chai in it
  x.fillStyle = '#f0c8a0'; for (const [i, j] of [[4, 6], [5, 5], [4, 4], [6, 6], [7, 5], [6, 4], [6, 3]]) x.fillRect(i, j, 1, 1);   // steam
  for (const [mx, my] of [[3, 16], [8, 16]]) { x.fillStyle = '#e8a848'; x.fillRect(mx, my - 1, 3, 1); x.fillRect(mx + 1, my - 2, 1, 1); x.fillStyle = '#b87828'; x.fillRect(mx, my, 3, 1); }
  sign(x, 'MANDAZI', 14, 3, '#fff0cc');
  sign(x, 'NA CHAI', 15, 10, '#ffd040');
  return gradeAd(c, { level: 0.74, tint: '#2a2048', mix: 0.14, wear: 0.15, seed: 61 });
}

/* ================= The train on the viaduct ================= */
// A container train for the Naivasha depot, its engine's headlight leading; boxes in faded reds, blues and greys.
const BOXES = ['#5a2620', '#24405a', '#4a4a52', '#2a4a34', '#6a5028', '#6a6a72', '#7a3a2a'];
function renderTrain(n, seed) {
  const r = rng(seed), wagon = 15, w = 18 + n * wagon, p = painter(w, 8);
  for (let i = 0; i < 18; i++) for (let y = 1; y < 7; y++) {                        // the engine, cab at the front (left)
    let col = y === 1 ? '#5a6a7a' : y < 4 ? '#3a4a5a' : '#24303e';
    if (i > 1 && i < 5 && y === 2) col = '#e8d8a0';
    if (y === 4) col = '#a83a2a';
    if (i === 0 && y > 1) col = '#4a5a6a';
    p.put(i, y, col);
  }
  p.put(0, 5, '#fff8e0');
  for (let k = 0; k < n; k++) {
    const x0 = 18 + k * wagon + 1, col = BOXES[Math.floor(r() * BOXES.length)], two = r() < 0.4;
    for (let i = x0; i < x0 + wagon - 1; i++) {
      p.put(i, 6, '#14141e');
      for (let y = 2; y < 6; y++) p.put(i, y, y === 2 ? mixHex(col, '#c8b0a0', 0.3) : (two && i === x0 + 7) ? '#14141e' : (i - x0) % 2 ? col : mixHex(col, '#000000', 0.2));
    }
  }
  return p.done();
}
/* ================= The loop's timeline (seconds) =================
   6-30, 128-150  egrets fly to roost over the city, left to right
   26-84     a container train for the Naivasha depot crosses the viaduct, right to left; its horn at 29
   38-66     the cow lowers her neck, splays her forelegs and drinks, lifting her head once to look round; rings
   58-78     the ranger sweeps the plain with his spotlight; a hyena's eyes shine by the bush (64-74)
   92-118    a light aircraft comes down to Wilson Airport, its landing light on
   124-176   a jet on the approach to JKIA crosses high, right to left
   150-186   the evening passenger train crosses the viaduct, left to right, its windows lit; its horn at 151
   158-186   the cow drinks again
   168-200   UAP Old Mutual Tower's LED facade turns to the Kenyan flag's colours, then back to green
   194-214   the second sweep; the hyena again (200-210)
   214-236   another light aircraft into Wilson
   all loop  the guide pours every 20 s, guest A sips every 12 s, the watcher's binoculars every 24 s, the bull chews,
             the giraffes' tails swish, fireflies, moths at the lantern, windows, Britam's LEDs, the grass in the wind */
const FREIGHT = { t0: 26, t1: 84 };
const PASSENGER = { t0: 150, t1: 186 };
const EGRETS = [[6, 30], [128, 150]];
const DRINKS = [[38, 66], [158, 186]];
const SWEEPS = [[58, 78], [194, 214]];
const WILSON = [[92, 118], [214, 236]];
const JET = { t0: 124, t1: 176 };
const FLAG = { t0: 168, t1: 200 };
const HYENA = { x: 150, y: 197, at: [[62, 76], [198, 212]] };
const COW_NP = 6;                                       // the cow's poses from standing (0) to drinking (COW_NP - 1)
const cowPose = u => ({ d: lerp(0, POSES.drink.d, u), head: [lerp(POSES.stand.head[0], POSES.drink.head[0], u), lerp(POSES.stand.head[1], POSES.drink.head[1], u)],
  muzzle: [lerp(POSES.stand.muzzle[0], POSES.drink.muzzle[0], u), lerp(POSES.stand.muzzle[1], POSES.drink.muzzle[1], u)],
  fl: u === 0 ? POSES.stand.fl : [lerp(0.78, POSES.drink.fl[0], u), lerp(1.02, POSES.drink.fl[1], u)], splay: u > 0, up: [lerp(-0.12, -0.08, u), lerp(0.24, -0.2, u)] });
function cowIndex(t) {                                  // lowers over 4 s, drinks, looks up once, raises over 4 s
  const w = DRINKS.find(([a, b]) => t >= a && t < b);
  if (!w) return 0;
  const [a, b] = w, top = COW_NP - 1, look = a + 13;
  let u = Math.min(1, (t - a) / 4, (b - t) / 4);
  if (t >= look && t < look + 4) u = Math.min(u, 0.45 + 0.55 * Math.abs((t - look - 2) / 2));
  return Math.round(u * top);
}

/* ================= Life: the sky's traffic, the passenger train, the spotlight, the mist and the grass ================= */
function renderPassenger(n) {                           // the evening train: a red-nosed engine, cream coaches, windows lit
  const coach = 16, w = 18 + n * coach, p = painter(w, 8);
  for (let i = 0; i < 18; i++) for (let y = 1; y < 7; y++) {
    let col = y < 3 ? '#a8a49c' : '#8a8680';
    if (i < 6) col = y === 1 ? '#c84030' : '#a03024';
    if (i > 6 && i < 10 && y === 2) col = '#f0e0b0';
    p.put(i, y, col);
  }
  p.put(0, 5, '#fff8e0');
  for (let k = 0; k < n; k++) {
    const x0 = 18 + k * coach + 1;
    for (let i = x0; i < x0 + coach - 1; i++) for (let y = 1; y < 7; y++) {
      let col = y === 1 ? '#a83a2c' : y === 6 ? '#24242c' : '#9a968e';
      if (y === 3 && (i - x0) % 3 !== 0) col = hash(i, k, 77) < 0.8 ? '#ffd890' : '#6a6458';
      p.put(i, y, col);
    }
  }
  return p.done();
}
function drawPassenger(t) {
  if (t < PASSENGER.t0 || t >= PASSENGER.t1) return;
  const u = (t - PASSENGER.t0) / (PASSENGER.t1 - PASSENGER.t0), x = Math.round(lerp(-PASS_C.width - 6, W + 6, u));
  ctx.drawImage(PASS_C, x + PASS_C.width, VIA - 8, -PASS_C.width, 8);   // engine leading, to the right
  halo(HL.head, x + PASS_C.width + 2, VIA - 3, 0.8);
}
function drawEgrets(t) {
  const w = EGRETS.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const u = (t - w[0]) / (w[1] - w[0]);
  ctx.fillStyle = '#d8d4e6';
  for (let k = 0; k < 5; k++) {
    const x = Math.round(lerp(-30, W + 30, u) - Math.abs(k - 2) * 7 + k), y = Math.round(lerp(92, 70, u) + Math.abs(k - 2) * 3 + Math.sin(u * 20 + k) * 0.8);
    const up = (step(t, 960) + k) % 2;
    ctx.globalAlpha = 0.85 * Math.min(1, u * 12, (1 - u) * 12);
    ctx.fillRect(x, y, 2, 1);
    if (up) { ctx.fillRect(x - 1, y - 1, 1, 1); ctx.fillRect(x + 2, y - 1, 1, 1); } else { ctx.fillRect(x - 1, y + 1, 1, 1); ctx.fillRect(x + 2, y + 1, 1, 1); }
  }
  ctx.globalAlpha = 1;
}
function drawPlanes(t) {
  const w = WILSON.find(([a, b]) => t >= a && t < b);   // a light aircraft coming down to Wilson, behind the acacia
  if (w) {
    const u = (t - w[0]) / (w[1] - w[0]), x = Math.round(lerp(236, BEACON.x - 6, u)), y = Math.round(lerp(66, BEACON.y - 4, u));
    const a = Math.min(1, u * 8, (1 - u) * 8);
    ctx.globalAlpha = a; R(x - 2, y, 5, 1, '#3a3a4c'); R(x, y - 1, 1, 1, '#3a3a4c');
    R(x - 2, y, 1, 1, frac(t) < 0.5 ? '#ff4a3a' : '#5a1a18'); R(x + 2, y, 1, 1, '#4aff7a'); ctx.globalAlpha = 1;
    halo(HL.landing, x - 2, y, 0.7 * a);
  }
  if (t >= JET.t0 && t < JET.t1) {                       // a jet on the approach to JKIA, high
    const u = (t - JET.t0) / (JET.t1 - JET.t0), x = Math.round(lerp(W + 10, -20, u)), y = Math.round(lerp(22, 44, u));
    R(x, y, 7, 1, '#4a4a5c'); R(x + 3, y - 1, 2, 1, '#4a4a5c');
    R(x, y, 1, 1, '#fff8e8'); halo(HL.landing, x, y, 0.8);
    if (frac(t / 1.5) < 0.12) R(x + 4, y - 1, 1, 1, '#ff5a4a');
  }
}
function drawMist(t) {
  for (const m of MIST_C) {
    const off = m.k > 0 ? scroll(t, m.k, W) : W - 1 - scroll(t, -m.k, W);
    ctx.globalAlpha = m.a * (0.85 + 0.15 * wave(t, 3, m.y / 50));
    ctx.drawImage(m.c, off - W, m.y); ctx.drawImage(m.c, off, m.y);
  }
  ctx.globalAlpha = 1;
}
function drawGrass(t) {
  for (let g = 0; g < 2; g++) {
    const v = 0.75 * wave(t, 20 + g * 6, g * 0.31) + 0.25 * wave(t, 52 + g * 10, 0.6);
    ctx.drawImage(GRASS_C[g][clamp(Math.round((v + 1) / 2 * (GRASS_NF - 1)), 0, GRASS_NF - 1)], 0, 200);
  }
}
// the ranger's spotlight: from his hand, out over the plain to the acacia and back, catching whatever is out there
const TORCH = { x: 365, y: 180 };
function sweepAt(t) {
  const w = SWEEPS.find(([a, b]) => t >= a && t < b);
  if (!w) return null;
  const u = (t - w[0]) / (w[1] - w[0]);
  return { x: lerp(318, 52, 0.5 - 0.5 * Math.cos(TAU * u)), y: Math.round(u < 0.5 ? 195 : lerp(195, 226, Math.sin(Math.PI * (u - 0.5) * 2))), a: Math.min(1, u * 10, (1 - u) * 10) };
}
function drawSpotlight(t, reducedMotion) {
  const sp = sweepAt(t);
  if (!sp) return;
  const wd = 5 + (TORCH.x - sp.x) * 0.07, g = ctx.createLinearGradient(TORCH.x, TORCH.y, sp.x, sp.y);
  g.addColorStop(0, 'rgba(255,240,200,0.42)'); g.addColorStop(1, 'rgba(255,236,190,0.14)');
  ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = sp.a;
  ctx.fillStyle = g; ctx.beginPath(); ctx.moveTo(TORCH.x, TORCH.y); ctx.lineTo(sp.x - wd, sp.y - 3); ctx.lineTo(sp.x + wd, sp.y + 3); ctx.closePath(); ctx.fill();
  ctx.globalCompositeOperation = 'source-over'; ctx.globalAlpha = 1;
  halo(HL.spot, sp.x, sp.y, sp.a * 0.9);
  for (const g2 of GIRAFFES) {                           // the beam lights a giraffe as it passes
    const d = Math.abs(sp.x - g2.x);
    if (d < 30) halo(HL.spotG, g2.x, g2.y - 2.4 * g2.s, sp.a * (1 - d / 30) * 0.8);
  }
}
function drawHyena(t) {                                  // a spotted hyena lying by the bush; its eyes shine in the beam
  const w = HYENA.at.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const sp = sweepAt(t), fade = Math.min(1, (t - w[0]) / 1.5, (w[1] - t) / 1.5);
  const lit = sp ? Math.max(0, 1 - Math.abs(sp.x - HYENA.x) / 36) : 0;
  const { x, y } = HYENA, body = mixHex('#2a2420', '#8a7458', lit * 0.6);
  ctx.globalAlpha = fade;
  R(x - 6, y - 2, 9, 3, body); R(x + 2, y - 4, 4, 3, body); R(x + 5, y - 3, 2, 2, body); R(x + 2, y - 5, 1, 1, body); R(x + 4, y - 5, 1, 1, body);
  ctx.globalAlpha = fade * (0.35 + 0.65 * lit);
  R(x + 3, y - 3, 1, 1, '#e8ff9a'); R(x + 5, y - 3, 1, 1, '#e8ff9a');
  ctx.globalAlpha = 1;
  if (lit > 0.2) halo(HL.fly, x + 4, y - 3, lit);
}
function drawCowRings(t) {                               // rings on the water where the cow drinks
  if (cowIndex(t) < COW_NP - 1) return;
  const cw = GIRAFFES[1], mx = Math.round(cw.x + POSES.drink.muzzle[0] * cw.s), my = POOL.bot - 3;
  ctx.fillStyle = '#c8c0e0';
  for (let k = 0; k < 2; k++) {
    const u = frac(t / 3 + k * 0.5), rx = 1 + u * 9, ry = 0.5 + u * 2;
    ctx.globalAlpha = 0.5 * (1 - u);
    for (let a = 0; a < 24; a++) ctx.fillRect(Math.round(mx + Math.cos(a / 24 * TAU) * rx), Math.round(my + Math.sin(a / 24 * TAU) * ry), 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Three hidden things (site/js/ui/secrets.js) =================
   a bush baby in the acacia (all loop), the hyena the spotlight finds (with the sweeps), a lioness lying on the far
   plain under the railway (all loop); small, in the scene's own light, nothing that blinks */
const GALAGO = { x: 66, y: 119 };                       // on the acacia's middle limb, under the canopy
const LIONESS = { x: 280, y: 184 };                     // on the far plain between two of the viaduct's piers
function drawGalago(t) {
  const { x, y } = GALAGO, fur = '#5a4a4a', dk = '#3a2e30', turn = frac(t / 16) < 0.3 ? 1 : 0;
  R(x + 1, y + 3, 3, 4, fur); R(x + 1, y + 3, 1, 4, '#6e5a58');                    // body, lit from the west
  R(x + turn, y, 4, 3, fur); R(x + turn, y - 1, 1, 1, dk); R(x + 3 + turn, y - 1, 1, 1, dk);   // head and ears
  R(x + turn, y + 1, 1, 1, '#e8c070'); R(x + 2 + turn, y + 1, 1, 1, '#e8c070');    // its big eyes catch the light
  R(x + 4, y + 6, 1, 2, dk); R(x + 5, y + 7, 1, 1, dk);                           // the tail hanging off the limb
}
function drawLioness(t) {
  const { x, y } = LIONESS, c = '#6a5440', d = '#4a3a2e', up = frac((t + 4) / 20) < 0.25 ? 1 : 0;
  R(x, y + 2, 10, 2, c); R(x + 1, y + 1, 7, 1, c);                                // lying, flank to us
  R(x + 9, y + 1 - up, 3, 2, c); R(x + 11, y + 2 - up, 1, 1, d);                  // her head, lifted now and then
  R(x - 2, y + 3, 2, 1, d); R(x - 3, y + 2 + (frac(t / 10) < 0.1 ? 1 : 0), 1, 1, '#2a2018');   // the tail and its tuft
}
/* ================= Small life ================= */
const FIREFLIES = [];
{ const r = rng(515); for (let i = 0; i < 40; i++) FIREFLIES.push({ x: 10 + r() * 300, y: 188 + r() * 48, k1: 3 + Math.floor(r() * 5), k2: 4 + Math.floor(r() * 6), kb: 30 + Math.floor(r() * 40), ph: r(), ph2: r() }); }
const BEACON = { x: 40, y: 151 };                       // Wilson Airport's beacon, low on the left

/* ================= Weather: no falling things of its own; the picks at three depths ================= */
const WEATHER = weatherPlans([]);
const LIGHTS = [{ x: LAMP.x, y: LAMP.y, rx: 56, ry: 36 }];
const LIGHTS_BB = [...LIGHTS, { x: BB.x + 23, y: BB.y - 4, rx: 26, ry: 16 }];
const WX_LAND = [[150, POOL.top], [POOL.top, FEET], [H + 4, H + 4]];
const WX = { land: WX_LAND, lights: LIGHTS };
const WX_BB = { land: WX_LAND, lights: LIGHTS_BB };

function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.82 && (d - 0.82) * 3.2 > bayer(x, y)) p.put(x, y, '#04050c', 90);
  }
  return p.done();
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, FAR_C, BRIT_C, UAP_C, TIMES_C, KICC_C, MID_C, EDGE_C, PLAIN_C, VIA_C, WATER_C, NEAR_C,
  ACACIA_C, GIR_C, CAR_C, TABLE_C, BB_C, BB_AD, PITCH, TRAIN_C, VIG_C, PASS_C, MIST_C, GRASS_C, COW_C, BULL2_C;
function prerender() {
  HL = {
    moon: makeHalo(30, '#f0d8a8', 0.16, 1.8), mast: makeHalo(16, '#5a8cff', 0.34, 1.5), mastCore: makeHalo(6, '#a8c8ff', 0.5, 1.3),
    red: makeHalo(4, '#ff3a30', 0.6, 1), crown: makeHalo(14, '#d8b8f0', 0.3, 1.5), kicc: makeHalo(26, '#ffa860', 0.14, 1.6),
    city: makeHalo(120, '#ff9a50', 0.06, 1.3), lamp: makeHalo(64, '#ffa850', 0.28, 1.7), lampCore: makeHalo(12, '#ffe0a0', 0.55, 1.4),
    fly: makeHalo(3, '#d8ff80', 0.5, 1), beacon: makeHalo(5, '#a8ffc8', 0.5, 1.2), head: makeHalo(18, '#fff4d0', 0.35, 1.3),
    bbLamp: makeHalo(6, '#ffc878', 0.3, 1.5), bbWash: makeHalo(30, '#ff9a60', 0.06, 1.5),
    landing: makeHalo(6, '#fff4e0', 0.45, 1.2), spot: makeHalo(26, '#fff0c8', 0.3, 1.4), spotG: makeHalo(26, '#ffe8c0', 0.16, 1.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp);
  { const sx = SKY_C.getContext('2d'); drawHalo(sx, HL.moon, MOON.x, MOON.y, 1); drawHalo(sx, HL.city, 260, 150, 1); }
  FAR_C = renderFarCity(); BRIT_C = renderBritam(); UAP_C = renderUAP(); TIMES_C = renderTimes(); KICC_C = renderKICC();
  MID_C = renderMidCity(); EDGE_C = renderEdge(); PLAIN_C = renderPlain(); VIA_C = renderViaduct();
  WATER_C = ditherFill(POOL.x1 - POOL.x0, POOL.bot - POOL.top, ['#121430', '#1a1c3a', '#242646', '#323054', '#463c62', '#5a4668'], (i, j) => 0.8 - j / 13 * 0.6 + (1 - i / 210) * 0.15);
  NEAR_C = renderNear(); ACACIA_C = renderAcacia();
  GIR_C = GIRAFFES.map((g, k) => buildGiraffe(g.s, g.pose, g.dir, 300 + k * 17, g.dark));
  CAR_C = renderCar(); TABLE_C = renderTable();
  BB_C = renderBillboard(); BB_AD = renderAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#ffd6a5' });
  TRAIN_C = renderTrain(20, 88);
  PASS_C = renderPassenger(8);
  MIST_C = MISTS.map(renderMist);
  GRASS_C = [0, 1].map(g => Array.from({ length: GRASS_NF }, (_, f) => renderGrass(g, f)));
  COW_C = Array.from({ length: COW_NP }, (_, k) => buildGiraffe(GIRAFFES[1].s, cowPose(k / (COW_NP - 1)), 1, 317, GIRAFFES[1].dark));
  BULL2_C = buildGiraffe(GIRAFFES[0].s, { ...POSES.browse, head: [1.43, 5.38], muzzle: [1.9, 5.64] }, 1, 300, GIRAFFES[0].dark);
  VIG_C = renderVignette();
}

/* ================= Per frame ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#eef2ff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.6 + 0.4 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.35; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(MOON_C, MOON.x - MOON.r - 1, MOON.y - MOON.r - 1);
  for (const L of WISP_C) {                              // drifting: cool all along, warm towards the west
    const off = scroll(t, L.k, W);
    for (const dx of [off - W, off]) ctx.drawImage(L.cool, dx, L.y);
    for (const [x0, x1, a] of [[0, 120, 1], [120, 200, 0.7], [200, 280, 0.4], [280, 340, 0.15]]) {
      ctx.save(); ctx.beginPath(); ctx.rect(x0, L.y, x1 - x0, L.cool.height); ctx.clip();
      ctx.globalAlpha = a;
      for (const dx of [off - W, off]) ctx.drawImage(L.warm, dx, L.y);
      ctx.restore();
    }
  }
  ctx.globalAlpha = 1;
}
function drawTowers(t, crowdUnused) {
  ctx.drawImage(FAR_C, 0, 0);
  halo(HL.kicc, KICC.cx, KICC.top + 14, 0.9);
  ctx.drawImage(KICC_C, 0, 0);
  ctx.drawImage(TIMES_C, 0, 0);
  ctx.drawImage(UAP_C, 0, 0);
  halo(HL.crown, UAP.cx, UAP.crown + 4, 0.8 + 0.1 * wave(t, 12));
  ctx.drawImage(BRIT_C, 0, 0);
  // Britam's mast lit blue, its beacon, the roof's corner lights, the LED dots chasing up its edges and creases
  halo(HL.mast, BRIT.cx, BRIT.tip + 15, 0.6 + 0.08 * wave(t, 8));
  halo(HL.mastCore, BRIT.cx, BRIT.tip + 14, 0.55);
  const blink = frac(t / 2) < 0.5;
  R(BRIT.cx, BRIT.tip - 1, 1, 1, blink ? '#ff4a3a' : '#6a1a18'); if (blink) halo(HL.red, BRIT.cx, BRIT.tip - 1, 0.8);
  R(BRIT.cx - britHW(BRIT.roof) + 1, BRIT.roof - 2, 1, 1, '#ff5a4a'); R(BRIT.cx + britHW(BRIT.roof) - 2, BRIT.roof - 2, 1, 1, '#ff5a4a');
  ctx.fillStyle = '#f4f8ff';
  for (const d of BRIT_DOTS) {
    ctx.globalAlpha = 0.6 + 0.4 * Math.max(0, wave(t, 30, -d.y / 26));
    ctx.fillRect(d.x, d.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  // UAP: the facade's green LED dots, the spire's ring light
  // its own green, and for a while the Kenyan flag (black, white, red, white, green), wiping up and back down
  const wipe = t >= FLAG.t0 && t < FLAG.t1 ? Math.min(1, (t - FLAG.t0) / 3, (FLAG.t1 - t) / 3) : 0;
  for (const d of UAP_DOTS) {
    const v = (UAP.base - d.y) / (UAP.base - UAP.body);
    let col = '#5ae87a', a = 0.35 + 0.25 * d.s;
    if (v < wipe) {
      const f = 1 - (d.y - UAP.body) / (UAP.base - UAP.body), band = f > 0.7 ? 0 : f > 0.65 ? 1 : f > 0.35 ? 2 : f > 0.3 ? 3 : 4;
      col = ['#3a3a44', '#f4f0f0', '#f0403a', '#f4f0f0', '#3ad06a'][band]; a = band === 0 ? 0.3 : 0.75;
    }
    ctx.globalAlpha = a; ctx.fillStyle = col; ctx.fillRect(d.x, d.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  R(UAP.cx - 1, UAP.crown - 1, 3, 1, '#fff4e0'); R(UAP.cx, UAP.tip - 1, 1, 1, blink ? '#6a1a18' : '#ff4a3a');
  R(TIMES.cx + 1, TIMES.top - 12, 1, 1, blink ? '#ff4a3a' : '#7a2018');
  R(KICC.cx, KICC.top - 2, 1, 1, '#ff4a3a'); halo(HL.red, KICC.cx, KICC.top - 2, 0.5);
}
// the Southern Bypass along the park's edge: headlights going one way, tail lights the other, under the city
const TRAFFIC = [];
{ const r = rng(612); for (let k = 0; k < 64; k++) TRAFFIC.push({ lane: k & 1, n: 2 + Math.floor(r() * 3), ph: r(), gap: 2 + Math.floor(r() * 2) }); }
function drawTraffic(t) {
  for (const c of TRAFFIC) {
    const u = frac(c.n * t / LOOP + c.ph), x = Math.round(c.lane ? lerp(-8, W + 8, u) : lerp(W + 8, -8, u)), y = c.lane ? 171 : 169;
    R(x, y, 1, 1, c.lane ? '#ff4a3a' : '#fff4d8'); R(x + c.gap, y, 1, 1, c.lane ? '#d83a2e' : '#ffe8b8');
  }
}
function drawCity(t) {
  ctx.drawImage(MID_C, 0, 0);
  for (const w of WINS) R(w.x, w.y, 1, 1, hash(w.x, step(t + w.s * 30, w.k), w.y) < 0.55 ? w.on : w.off);
  ctx.drawImage(EDGE_C, 0, 0);
  drawTraffic(t);
  const on = frac(t / 2) < 0.5;                         // Wilson's beacon: white, then green
  R(BEACON.x, BEACON.y, 1, 1, on ? '#f4fff8' : '#5aff9a'); halo(HL.beacon, BEACON.x, BEACON.y, 0.7);
}
function drawTrain(t) {
  if (t < FREIGHT.t0 || t >= FREIGHT.t1) return;
  const u = (t - FREIGHT.t0) / (FREIGHT.t1 - FREIGHT.t0), x = Math.round(lerp(W + 6, -TRAIN_C.width - 6, u));
  ctx.drawImage(TRAIN_C, x, VIA - 8);
  halo(HL.head, x - 2, VIA - 3, 0.8);
  ctx.fillStyle = '#fff4d0';
  for (let k = 1; k < 16; k++) { ctx.globalAlpha = 0.28 * (1 - k / 16); ctx.fillRect(x - k * 2, VIA - 4 + (k > 8 ? 1 : 0), 2, 1); }
  ctx.globalAlpha = 1;
}
function drawWater(t) {                                 // the waterhole: the sky's glow, the city's lights mirrored, streaks
  ctx.drawImage(WATER_C, POOL.x0, POOL.top);
  reflect(ctx, t, { top: POOL.top, rows: POOL.bot - POOL.top, squash: 2.2, alpha: 0.72, x: POOL.x0, w: POOL.x1 - POOL.x0, k1: 60, k2: 150, amp: 0.4, grow: 0.04 });
  streak(ctx, t, BRIT.cx, 5, '#a8c4ff', 0.42, 3, POOL.top + 1, POOL.bot);
  streak(ctx, t, UAP.cx, 4, '#e0c8f0', 0.32, 11, POOL.top + 1, POOL.bot);
  for (const [x, c, sd] of [[112, '#ffc070', 21], [166, '#ffd890', 23], [214, '#ffb060', 27], [262, '#ffd080', 29], [284, '#c8d8ff', 31]])
    streak(ctx, t, x, 3, c, 0.3, sd, POOL.top + 1, POOL.bot);
}
function drawGiraffes(t) {
  GIRAFFES.forEach((g, k) => {
    // the bull chews (his head nudges up in the canopy now and then), the cow drinks on her schedule
    const G = k === 0 ? (hash(5, step(t, 160)) < 0.4 ? BULL2_C : GIR_C[0]) : k === 1 ? COW_C[cowIndex(t)] : GIR_C[k];
    ctx.drawImage(G.c, Math.round(g.x - G.ox), Math.round(g.y - G.oy));
    const [tx, ty] = G.tail, sw = Math.round(1.6 * wave(t, 20 + k * 4, k * 0.3));   // the tail swishes
    const x0 = g.x - G.ox + tx, y0 = g.y - G.oy + ty, len = Math.round(g.s * 0.95);
    ctx.fillStyle = mixHex(GIR.patch, NIGHT, g.dark);
    for (let j = 0; j < len; j++) ctx.fillRect(Math.round(x0 - g.dir * (1 + Math.max(0, sw) * j / len) + Math.min(0, sw) * j / len), Math.round(y0 + j), 1, 1);
    ctx.fillStyle = '#100a08'; ctx.fillRect(Math.round(x0 - g.dir * 1 + sw * 0.8), Math.round(y0 + len), 2, 2);
  });
}
const HATCH = look(34, 3, 1, '#d8d0b8', '#4a4a3a');
// what each of the stop's people is doing at t: sips, pours, binoculars up and down, the torch during the sweeps
function castPose(c, t) {
  const o = { ...c.o };
  if (c.id === 'guestA') o.aR = frac(t / 12) < 0.22 ? 5 : 3;
  else if (c.id === 'watcher') { const q = frac((t + 5) / 24); o.aR = q < 0.68 ? 2 : q < 0.8 ? 3 : 0; }
  else if (c.id === 'guide') o.aR = frac((t + 8) / 20) < 0.25 ? 3 : 0;
  else if (c.id === 'guestB') { const q = frac((t + 3) / 16); o.aR = q < 0.5 ? 3 : 0; o.view = q > 0.75 ? 1 : 2; }
  else if (c.id === 'ranger') o.aR = sweepAt(t) ? 3 : 0;
  return o;
}
function drawStop(t, crowd) {
  const DL = [];
  const put = (y, f) => DL.push([y, f]);
  put(FEET - 0.5, () => ctx.drawImage(TABLE_C, 0, 0));
  if (crowd > 0.4 && frac((t + 8) / 20) < 0.25) put(203.5, () => { R(TABLE.x + 20, 178, 2, 6, '#b8c4d8'); R(TABLE.x + 20, 178, 2, 1, '#2a2a34'); R(TABLE.x + 21, 184, 1, 2, '#c87a3a'); });   // the guide's flask, pouring chai
  put(CAR.y, () => {
    ctx.globalAlpha = 0.6; R(CAR.x + 3, CAR.y, CAR_W - 4, 2, '#05050a'); R(CAR.x + 8, CAR.y + 2, CAR_W - 14, 1, '#05050a'); ctx.globalAlpha = 1;
    ctx.drawImage(CAR_C, CAR.x, CAR.y - CAR_H + 1);
  });
  for (const c of CAST) if (c.rank < crowd) put(c.y + (c.o.sit ? 9 : 0), () => person(c.x, c.y, c.P, castPose(c, t)));
  if (crowd > 0.7) put(CAR.y + 0.5, () => {             // at Busy, a guest standing up through the roof with a camera
    ctx.save(); ctx.beginPath(); ctx.rect(CAR.x + 31, CAR.y - CAR_H + 5, 56, 11); ctx.clip();
    person(CAR.x + 52, 190, HATCH, { view: 2, dir: -1, aR: frac(t / 30) < 0.6 ? 2 : 0, lit: -1 });
    ctx.restore();
  });
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, f] of DL) f();
  // the hurricane lantern on the table: its glass, its flame breathing
  const fl = 0.9 + 0.06 * wave(t, 300) + 0.04 * (hash(1, step(t, 2400)) - 0.5), L = LAMP;
  R(L.x - 3, L.y + 5, 7, 2, '#3a2a1a'); R(L.x - 3, L.y - 4, 1, 9, '#3a2a1a'); R(L.x + 3, L.y - 4, 1, 9, '#3a2a1a');   // the frame and its base
  R(L.x - 2, L.y - 4, 5, 9, '#ffd890'); R(L.x - 1, L.y - 2, 3, 5, '#fff2c8'); R(L.x, L.y - 1, 1, 3, '#ffffff');      // the glass, the flame
  R(L.x - 3, L.y - 5, 7, 1, '#2a2018'); R(L.x - 2, L.y - 6, 5, 1, '#3a2a1a'); R(L.x - 1, L.y - 7, 3, 1, '#2a2018');  // the cap
  R(L.x - 3, L.y - 10, 1, 3, '#2a2018'); R(L.x + 3, L.y - 10, 1, 3, '#2a2018'); R(L.x - 2, L.y - 11, 5, 1, '#2a2018');   // the handle
  halo(HL.lamp, L.x, L.y, fl); halo(HL.lampCore, L.x, L.y, fl);
  for (let k = 0; k < 3; k++) {                          // moths round the lantern
    const a = TAU * (t * (0.7 + k * 0.23) / 2 + k / 3), mx = L.x + Math.round(Math.cos(a) * (5 + k)), my = L.y - 4 + Math.round(Math.sin(a * 1.3) * 3);
    R(mx, my, 1, 1, '#e8d8b8');
  }
}
function drawFireflies(t) {
  for (const f of FIREFLIES) {
    const b = Math.pow(Math.max(0, wave(t, f.kb, f.ph)), 8);
    if (b < 0.05) continue;
    const x = Math.round(f.x + 6 * wave(t, f.k1, f.ph)), y = Math.round(f.y + 3 * wave(t, f.k2, f.ph2));
    ctx.globalAlpha = b; R(x, y, 1, 1, '#e0ff90'); ctx.globalAlpha = 1;
    halo(HL.fly, x, y, b);
  }
}
function drawBillboard(t, pitch) {
  halo(HL.bbWash, BB.x + 23, BB.y + 6, 0.8);
  ctx.drawImage(BB_C, 0, 0);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  for (const lx of BB_LAMPS) { R(lx - 1, BB.y - 4, 3, 1, '#ffe2b0'); halo(HL.bbLamp, lx, BB.y - 3, 0.9); }
}

/* ================= Frame compositor (back to front) ================= */
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  drawTowers(t);
  drawCity(t);
  drawPlanes(t);
  drawEgrets(t);
  if (billboard) drawBillboard(t, pitch);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.drawImage(PLAIN_C, 0, VIA);
  ctx.drawImage(VIA_C, 0, 0);
  drawTrain(t);
  drawPassenger(t);
  drawWater(t);
  ctx.drawImage(NEAR_C, 0, 0);
  ctx.drawImage(ACACIA_C, 0, 0);
  drawGalago(t);
  drawGiraffes(t);
  drawCowRings(t);
  drawHyena(t);
  drawLioness(t);
  drawMist(t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawSpotlight(t, reducedMotion);
  drawStop(t, crowd);
  drawGrass(t);
  drawFireflies(t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'nairobi',
  name: 'Nairobi',
  tagline: 'Giraffes under the city lights',
  timeZone: 'Africa/Nairobi',
  accent: '#ffd6a5',
  focusX: 247,
  landmark: [BRIT.cx - BRIT.hw - 1, KICC.cx + KICC.sw + 1],   // Britam Tower to the KICC
  landmarkTop: BRIT.tip - 1,                                  // Britam's beacon
  billboard: BB,
  crowd: CROWD,
  spots: [                                              // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: BRIT.cx - BRIT.hw, y: BRIT.tip - 1, w: 2 * BRIT.hw, h: 100 },                       // Britam Tower
    { kind: 'landmark', x: KICC.cx - KICC.sw, y: KICC.top - 1, w: 2 * KICC.sw + 1, h: 33 },                    // the KICC
    { kind: 'water', x: 170, y: POOL.top + 2, w: 104, h: 8 },                                                  // the waterhole
    { kind: 'window', x: LAMP.x - 3, y: LAMP.y - 11, w: 7, h: 18 },                                            // the lantern
  ],
  ambience: { crickets: 0.55, wind: 0.12, traffic: 0.1, water: 0.06, crowd: 0.06, birds: 0.04 },
  events: [
    { t: FREIGHT.t0 + 1, type: 'train', dur: FREIGHT.t1 - FREIGHT.t0 - 2, pan: [0.9, -0.9], gain: 0.35 }, { t: FREIGHT.t0 + 3, type: 'horn', x: 450, gain: 0.25 },
    { t: PASSENGER.t0 + 1, type: 'train', dur: PASSENGER.t1 - PASSENGER.t0 - 2, pan: [-0.9, 0.9], gain: 0.3 }, { t: PASSENGER.t0 + 2, type: 'horn', x: 30, gain: 0.22 },
    ...DRINKS.map(([a]) => ({ t: a + 5, type: 'splash', x: 222, gain: 0.12 })),
    { t: FLAG.t0, type: 'chime', x: UAP.cx, gain: 0.25 },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
